import { SettingsService } from '@/services/settings'
import { useAlertStore } from '@/stores/alert'
import { useCampaignsStore } from '@/stores/campaigns'
import { usePlansStore } from '@/stores/plans'
import { useUserStore } from '@/stores/user'
import { useUserProgressionStore } from '@/stores/user-progression'
import type {
  Campaign,
  NavLinkCategory,
  Nullable,
  Plan,
  Prize,
  Rank,
  User,
  UserProgression,
} from '@/types'
import { isInPast } from '@/utils/date-time-utils'
import { isLeaderboardEnabled } from '@/utils/feature-flags'
import { Icons } from '@/utils/helper-objects'
import { isPartnershipCampaign } from '@/utils/type-guard-utils'
import { isEmpty } from '@/utils/utils'
import { useUrlSearchParams } from '@vueuse/core'
import { useCookies } from '@vueuse/integrations/useCookies.mjs'
import console from 'console'
import type { RouteRecordProps } from 'env'
import {
  type NavigationGuardNext,
  type RouteLocation,
  type RouteRecordRaw,
  useRouter,
} from 'vue-router'
import { useToast } from 'vue-toastification'

import HomeLayoutProvider from '../views/Home/HomeLayoutProvider.vue'
import dailySpinsRoutes from './daily-spins-routes'
import { referralRedirectGuard } from './guards/referral-redirect'
import instantWinRoutes from './instant-win-routes'
import membersZoneRoutes from './members-zone-routes'
import termsAndConditionsRoutes from './terms-and-conditions-routes'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeLayoutProvider,
    meta: {
      nav: {
        sidebar: {
          display: true,
          text: 'Home',
          icon: Icons.Home,
          category: 'default',
          order: 0,
        },
        bottom: {
          display: true,
          text: 'nav.bottom.home',
          icon: Icons.Home,
        },
      },
    },
  },
  ...membersZoneRoutes,
  {
    path: '/product',
    name: 'product',
    component: () => import('../views/ProductView.vue'),
  },
  {
    path: '/subscription-platinum-35',
    name: 'affiliate-subscription-platinum-35',
    component: () =>
      import('../views/Affiliates/SubscriptionPlatinum35View.vue'),
  },
  {
    path: '/benefits',
    name: 'benefits',
    component: () => import('../views/BenefitsView.vue'),
    meta: {
      title: 'Membership Benefits',
      nav: {
        sidebar: {
          display: true,
          icon: Icons.Ticket,
          text: 'Benefits',
          category: 'third',
          order: 1,
        },
        footer: {
          display: true,
          text: 'Benefits',
          category: 'company',
        },
      },
    },
  },
  {
    path: '/leaderboard',
    name: 'leaderboard',
    component: () => import('../views/LeaderboardView.vue'),
    meta: {
      title: 'Weekly Race',
      nav: {
        sidebar: {
          display: isLeaderboardEnabled,
          text: 'Weekly Race',
          icon: Icons.LeaderBoard,
          category: 'default',
          order: 8,
        },
        footer: {
          display: isLeaderboardEnabled,
          text: 'Weekly Race',
          category: 'company',
        },
      },
    },
    beforeEnter: (_to, _from, next) => {
      if (isLeaderboardEnabled()) {
        next()
      } else {
        next({ name: 'not-found' })
      }
    },
  },
  {
    path: '/prizes',
    name: 'prizes',
    component: () => import('../views/PrizesView.vue'),
    meta: {
      title: 'Prizes',
      nav: {
        footer: {
          display: true,
          text: 'Prizes',
          category: 'company',
        },
      },
    },
  },
  {
    path: '/prize/:prizeCode/:listId?',
    name: 'prize',
    component: () => import('../views/PrizeView.vue'),
    meta: {
      title: (
        to: RouteLocation & RouteRecordProps<{ prize: Prize; listId: string }>
      ): string => {
        return `Prize: ${to.meta.props.prize.title}`
      },
    },
    props: (route: RouteRecordProps<{ prize: Prize; listId: string }>) => {
      return {
        prize: route.meta.props.prize,
        listId: route.meta.props.listId,
      }
    },
  },
  {
    path: '/results-and-winners',
    name: 'results-and-winners',
    component: () => import('../views/ResultsView.vue'),
    meta: {
      title: 'Results + Winners',
      nav: {
        sidebar: {
          display: false,
        },
        footer: {
          display: true,
          text: 'Results + Winners',
          category: 'company',
        },
      },
    },
  },
  {
    path: '/live-draws/:prizeCode',
    name: 'live-draw',
    component: () => import('../views/LiveDrawView.vue'),
    meta: {
      title: (
        to: RouteLocation & RouteRecordProps<{ prize: Prize }>
      ): string => {
        return `Live Draw: ${to.meta.props?.prize?.title}`
      },
    },
    props: (route: RouteRecordProps<{ prize: Prize }>) => {
      return {
        prize: route.meta.props.prize,
      }
    },
  },
  {
    path: '/thank-you',
    name: 'thank-you',
    component: () => import('../views/ThankYouView.vue'),
    meta: {
      title: 'Thank You',
    },
  },
  {
    path: '/good-causes',
    name: 'good-causes',
    component: () => import('../views/SponsorshipView.vue'),
    meta: {
      title: 'Good Causes',
      nav: {
        sidebar: {
          display: true,
          text: 'Good Causes',
          icon: Icons.Diversity,
          category: 'third',
          order: 3,
        },
        footer: {
          display: true,
          text: 'Good Causes',
          category: 'company',
        },
      },
    },
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import('../views/faq/FaqView.vue'),
    meta: {
      title: 'Frequently Asked Questions',
      nav: {
        sidebar: {
          display: true,
          text: 'FAQ',
          icon: Icons.Question,
          category: () => {
            return useUserStore().isLoggedIn ? 'members-zone' : 'second'
          },
          order: 9,
        },
        footer: {
          display: true,
          text: 'FAQ',
          category: 'support',
        },
      },
    },
  },
  {
    path: '/faq/:slug',
    name: 'faq-details',
    component: () => import('../views/faq/FaqDetails.vue'),
    meta: {
      title: 'Frequently Asked Questions',
      nav: {
        footer: {
          display: false,
        },
      },
    },
  },
  {
    path: '/campaign/:campaignCode/:listId?',
    name: 'campaign',
    component: () => import('../views/CampaignView.vue'),
    meta: {
      title: (
        to: RouteLocation & RouteRecordProps<{ campaign: Campaign }>
      ): string => {
        return `Campaign: ${to.meta.props.campaign.name}`
      },
    },
    props: (
      route: RouteRecordProps<{ campaign: Campaign; listId?: string }>
    ) => {
      return {
        campaign: route.meta.props.campaign,
        listId: route.meta.props.listId,
      }
    },
    async beforeEnter(
      to: RouteLocation,
      _from: RouteLocation,
      next: NavigationGuardNext
    ) {
      try {
        const campaignCode = to.params.campaignCode as string | undefined
        const toast = useToast()

        if (!campaignCode) {
          return next({ name: 'not-found' })
        }

        const campaignStore = useCampaignsStore()
        const queryParams = useUrlSearchParams()

        const campaign = await campaignStore.fetchCampaign(campaignCode)

        if (!campaign) {
          toast.error('Campaign not found.')
          return next({ name: 'not-found' })
        }

        // Check if campaign close date is in the past
        if (campaign.close_at && isInPast(campaign.close_at)) {
          toast.error('This campaign has ended.')
          return next({ name: 'home' })
        }

        let listId

        if (!isEmpty(queryParams._list)) {
          listId = queryParams._list as string
        } else if (!isEmpty(to.params.listId)) {
          listId = to.params.listId as string
        }

        to.meta = {
          ...to.meta,
          nav: {
            header: {
              displayLinks: !isPartnershipCampaign(campaign),
              logoImage: isPartnershipCampaign(campaign)
                ? campaign.logo_image
                : undefined,
            },
          },
          props: {
            campaign,
            listId,
          },
        }

        next()
      } catch (error) {
        console.error('Campaign guard failed:', error)
        next({ name: 'not-found' })
      }
    },
  },
  {
    path: '/membership-levels-20',
    name: 'membership-levels-20',
    component: () => import('../views/Discounts/Memberships20View.vue'),
    meta: {
      title: 'Membership Levels 20% Off',
    },
  },
  {
    path: '/product-vip-50',
    name: 'product-vip-50',
    component: () => import('../views/Discounts/MembershipVip50View.vue'),
    meta: {
      title: '50% Off Vip Membership',
    },
  },
  {
    path: '/memberships',
    name: 'memberships',
    component: () => import('../views/MembershipsView.vue'),
    meta: {
      title: 'Memberships',
    },
  },
  {
    path: '/secure-checkout',
    name: 'checkout',
    component: () => import('../views/Checkout/CheckoutView.vue'),
    meta: {
      title: 'Checkout',
    },
    props: (
      route: RouteRecordProps<{ plan: Plan; useSolidgateModal: boolean }>
    ) => {
      const props = route.meta.props
      return {
        plan: props.plan,
        useSolidgateModal: props.useSolidgateModal,
      }
    },
    async beforeEnter(
      to: RouteLocation,
      _from: RouteLocation,
      next: NavigationGuardNext
    ) {
      try {
        const plansStore = usePlansStore()
        const userStore = useUserStore()

        let plan: Nullable<Plan> = plansStore.getPlanByTier('standard')

        if (!isEmpty(to.query.plan)) {
          plan = plansStore.getPlan(
            to.query.plan as string,
            Number(to.query.affiliate as string)
          )
        }

        if (!plan) {
          console.error('no plan found')
          return next({ name: 'not-found' })
        }

        // Check if user should use Solidgate based on feature flag and user platform
        const shouldUseSolidgate = userStore.shouldUseSolidgate

        to.meta.props = {
          plan,
          useSolidgateModal: shouldUseSolidgate,
        }

        next()
      } catch (e) {
        console.error(e)
        // redirect to NotFoundView
        return next({ name: 'not-found' })
      }
    },
  },
  {
    path: '/your-level',
    name: 'your-level',
    component: () => import('../views/UserProgressionView.vue'),
    meta: {
      title: `${import.meta.env.GLP_BUSINESS_NAME} Points`,
      nav: {
        sidebar: {
          display: true,
          text: `${import.meta.env.GLP_BUSINESS_NAME} Points`,
          icon: Icons.Spaceship,
          category: (): NavLinkCategory => {
            return useUserStore().isLoggedIn ? 'members-zone' : 'second'
          },
          order: () => {
            return useUserStore().isLoggedIn ? 1 : 0
          },
        },
        footer: {
          display: true,
          text: `${import.meta.env.GLP_BUSINESS_NAME} Points`,
          category: 'company',
        },
      },
    },
    props: (
      route: RouteRecordProps<{
        ranks: Array<Rank>
        user?: Pick<User, 'first_name' | 'last_name'>
        userProgression?: UserProgression
      }>
    ) => {
      return {
        ranks: route.meta.props.ranks,
        user: route.meta.props.user,
        userProgression: route.meta.props.userProgression,
      }
    },
    async beforeEnter(
      to: RouteLocation,
      _from: RouteLocation,
      next: NavigationGuardNext
    ) {
      try {
        const userStore = useUserStore()
        const userProgressionStore = useUserProgressionStore()

        // fetch ranks
        const ranks = await userProgressionStore.fetchRanks()

        to.meta.props = {
          userProgression: userStore.user?.user_progression,
          user: {
            first_name: userStore.user?.first_name,
            last_name: userStore.user?.last_name,
          },
          ranks,
        }

        next()
      } catch (e) {
        console.error(e)
        // redirect to NotFoundView
        return next({ name: 'not-found' })
      }
    },
  },
  {
    path: '/payment-recovery',
    name: 'payment-recovery',
    component: () => import('../views/PaymentRecoveryView.vue'),
    meta: {
      title: 'Payment Recovery',
    },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../views/PasswordResetView.vue'),
    meta: {
      title: 'Password Reset',
    },
  },
  {
    path: '/referral-box/golden-box',
    name: 'referral-box-golden-box',
    component: () => import('../views/MembersZone/ReferralGoldenBoxView.vue'),
    meta: {
      title: 'Golden Referral Box',
      nav: {
        header: { display: false },
        footer: { display: false },
      },
    },
    props: (route: {
      meta: {
        props: {
          user: User
        }
      }
    }) => {
      return {
        user: route.meta.props.user,
      }
    },
    async beforeEnter(
      to: RouteLocation,
      _from: RouteLocation,
      next: NavigationGuardNext
    ) {
      try {
        const userStore = useUserStore()

        if (!userStore.isLoggedIn || !userStore.user) {
          return next({ name: 'login' })
        }

        to.meta.props = {
          user: userStore.user,
        }

        next()
      } catch (e) {
        console.error(e)
        // redirect to NotFoundView
        return next({ name: 'not-found' })
      }
    },
  },
  {
    path: '/referral-box',
    name: 'referral-box',
    component: () => import('../views/ReferralBoxView.vue'),
    meta: {
      title: 'Referral Box',
      nav: {
        header: { display: false },
        footer: { display: false },
      },
    },
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import('../views/WelcomeOfferView.vue'),
    meta: {
      title: 'Welcome',
      nav: {
        header: { display: false },
        footer: { display: false },
      },
    },
  },
  {
    path: '/request-password-reset',
    name: 'request-password-reset',
    component: () => import('../views/PasswordResetRequestView.vue'),
    meta: {
      title: 'Password Reset',
    },
  },
  {
    path: '/request-password-reset',
    name: 'request-password-reset-immediate',
    component: () => import('../views/PasswordResetRequestImmediateView.vue'),
    meta: {
      title: 'Password Reset',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: {
      title: 'Login',
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      title: 'Logout',
    },
    beforeEnter: async () => {
      const alertStore = useAlertStore()

      try {
        const userStore = useUserStore()
        const router = useRouter()
        const success = await userStore.logOut()

        if (success) {
          await router.push({ name: 'home' })
        } else {
          alertStore.addErrorAlert()
        }
      } catch (e: any) {
        alertStore.addErrorAlert({ message: e.message })
      }
    },
  },
  ...instantWinRoutes,
  ...dailySpinsRoutes,
  ...termsAndConditionsRoutes,
  {
    path: '/impersonate',
    name: 'impersonate',
    beforeEnter: async (to: RouteLocation) => {
      const router = useRouter()
      const alertStore = useAlertStore()
      const cookies = useCookies(['authToken', 'impersonated'], {
        autoUpdateDependencies: true,
      })

      if (!to.query.token) {
        alertStore.addErrorAlert({ message: 'Invalid token' })

        await router.push({ name: 'home' })
      }

      cookies.set('authToken', to.query.token, { path: '/' })
      cookies.set('impersonated', true, { path: '/' })

      await router.push({ name: 'members-zone' })
    },
  },
  {
    path: '/r/:referralValue',
    name: 'customer-referral-redirect',
    beforeEnter: referralRedirectGuard('referralValue'),
  },
  {
    path: '/share/:referralValue',
    name: 'lead-referral-redirect',
    beforeEnter: referralRedirectGuard('referralValue'),
  },
  {
    // https://router.vuejs.org/guide/essentials/dynamic-matching#Catch-all-404-Not-found-Route
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    // // preserve current path and remove the first char to avoid the target URL starting with `//`
    // params: { pathMatch: route.path.substring(1).split('/') },
    // // preserve existing query and hash if any
    // query: route.query,
    // hash: route.hash,
  },
] as Readonly<RouteRecordRaw[]>

export default routes.filter(
  (route) => !SettingsService.disabledRoutes.includes(route.name as string)
)
